/**
 * Minimal i18n — Hebrew (default, sync), English/Arabic/Russian (lazy).
 *
 * Hebrew is bundled on the entry chunk because ~95% of users are Israeli
 * Hebrew speakers. Other languages live in their own modules under
 * ./i18n/<lang>.ts and are dynamic-imported on demand by loadLanguage(),
 * which the AuthContext calls when a non-he language is selected.
 *
 * t() always returns synchronously. Before a non-he pack has loaded,
 * lookups for that language fall back to Hebrew automatically — so the
 * UI never blanks while a pack is being fetched.
 */
import he from './i18n/he';
import type { Lang, Translations } from '../types';

const translations: Partial<Record<Lang, Translations>> = { he };

/** Triggered loads, so multiple callers share a single fetch. */
const loading: Partial<Record<Lang, Promise<void>>> = {};

export function loadLanguage(lang: Lang): Promise<void> {
  if (lang === 'he' || translations[lang]) return Promise.resolve();
  if (loading[lang]) return loading[lang]!;
  const p =
    lang === 'en' ? import('./i18n/en') :
    lang === 'ar' ? import('./i18n/ar') :
    lang === 'ru' ? import('./i18n/ru') :
    Promise.resolve({ default: he });
  loading[lang] = p
    .then(mod => { translations[lang] = (mod as { default: Translations }).default; })
    .catch(() => { /* fallback to he silently */ });
  return loading[lang]!;
}

export function t(key: string, lang: Lang = 'he'): string {
  return translations[lang]?.[key] ?? he[key] ?? key;
}

export function isRTL(lang: Lang): boolean {
  return lang === 'he' || lang === 'ar';
}

export const LANGS: Record<Lang, { label: string; flag: string }> = {
  he: { label: 'עברית', flag: '🇮🇱' },
  en: { label: 'English', flag: '🇺🇸' },
  ar: { label: 'العربية', flag: '🇸🇦' },
  ru: { label: 'Русский', flag: '🇷🇺' },
};
